import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DataTable, type DataTableColumn } from "@h2j-data-team/ui";
import type { CapacityDailyTotal, CapacitySource } from "@repo/shared";

import { noop, STATIC_TABLE_STATE } from "@/components/qvd-analysis/qvd-list-utils";
import { useFormatDate } from "@/hooks/use-format-date";
import { formatBytes } from "@/lib/health-format";
import { tableMessages } from "@/lib/ui-labels";
import { useTableDensity } from "@/lib/use-table-density";

const SOURCE_ORDER: CapacitySource[] = ["datafile", "app_import", "app_reload"];

type Day = { date: string; bySource: Record<CapacitySource, number>; total: number };

/**
 * The month's daily series, already aggregated in the snapshot (date × origin),
 * folded into one row per day. Not paginated — it is at most ~31 rows — so the
 * rows are simply listed, newest first.
 */
export function CapacityByDayTable({ dailyTotals }: { dailyTotals: CapacityDailyTotal[] }) {
  const { t } = useTranslation("capacity");
  const { formatDate } = useFormatDate();
  const { density } = useTableDensity();

  const days = useMemo<Day[]>(() => {
    const byDate = new Map<string, Record<CapacitySource, number>>();
    for (const entry of dailyTotals) {
      const row = byDate.get(entry.date) ?? { datafile: 0, app_import: 0, app_reload: 0 };
      row[entry.source] += entry.bytes;
      byDate.set(entry.date, row);
    }
    return [...byDate.entries()]
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([date, bySource]) => ({
        date,
        bySource,
        total: SOURCE_ORDER.reduce((sum, s) => sum + bySource[s], 0),
      }));
  }, [dailyTotals]);

  const columns = useMemo<DataTableColumn<Day>[]>(
    () => [
      {
        id: "date",
        header: t("byDay.cols.date"),
        rowHeader: true,
        // Date-only value: pin it to local midnight so a UTC parse doesn't
        // shift it to the previous day west of Greenwich.
        cell: (day) => formatDate(`${day.date}T00:00:00`),
      },
      ...SOURCE_ORDER.map(
        (source): DataTableColumn<Day> => ({
          id: source,
          header: t(`source.${source}`),
          // Fits "IMPORTAÇÃO DE APP" with the sort icon.
          width: 180,
          align: "end",
          variant: "numeric",
          cell: (day) => (
            <span className="text-dim">
              {day.bySource[source] > 0 ? formatBytes(day.bySource[source]) : "—"}
            </span>
          ),
        }),
      ),
      {
        id: "total",
        header: t("byDay.cols.total"),
        width: 130,
        align: "end",
        variant: "numeric",
        cell: (day) => formatBytes(day.total),
      },
    ],
    [t, formatDate],
  );

  return (
    <DataTable
      label={t("tabs.byDay")}
      density={density}
      columns={columns}
      rows={days}
      getRowId={(day) => day.date}
      state={STATIC_TABLE_STATE}
      onStateChange={noop}
      messages={tableMessages({ empty: { title: t("byDay.empty") } })}
    />
  );
}
